import { useNavigate, useLocation } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { useEffect, useState } from "react";

export default function FaceRecognized() {
  usePageMeta("Face Recognized");
  const navigate = useNavigate();
  const location = useLocation();
  const { capturedImage, accountId, similarity } = location.state || {};
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Auto-navigate after 5 seconds
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Navigate when countdown reaches 0 (separate effect to avoid render issues)
    const navigateTimer = setTimeout(() => {
      navigate("/checkin/success" + window.location.search, {
        state: {
          accountId,
          firstName: location.state?.firstName,
          lastName: location.state?.lastName,
          similarity
        }
      });
    }, 5000);

    return () => {
      clearInterval(timer);
      clearTimeout(navigateTimer);
    };
  }, [navigate, accountId, similarity, location.state]);

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center p-3 overflow-hidden">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {/* Success Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-12 h-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-xl font-bold text-center mb-2 text-green-600">
          Face Recognition Successful!
        </h1>

        <p className="text-center text-gray-600 mb-4">
          Welcome back! Your identity has been verified.
        </p>

        {/* Recognized Photo Circle */}
        <div className="flex justify-center mb-4">
          <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-green-300">
            {capturedImage ? (
              <img 
                src={capturedImage} 
                alt="Recognized face" 
                className="w-full h-full object-cover"
              />
            ) : (
              <svg
                className="w-16 h-16 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Recognition Details */}
        {(similarity || accountId) && (
          <div className="bg-green-50 p-3 rounded-lg mb-4">
            <div className="text-sm text-green-800">
              {similarity && <div className="font-semibold">Match Confidence: {similarity.toFixed(1)}%</div>}
              {accountId && <div>Account ID: {accountId}</div>}
            </div>
          </div>
        )}

        {/* Auto-redirect notice */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600">
            Redirecting to check-in in {countdown} seconds...
          </p>
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          <button
            onClick={() => navigate("/checkin/success" + window.location.search)}
            className="w-full bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold text-sm hover:bg-[#b91900] transition-colors"
          >
            CONTINUE TO CHECK IN
          </button>

          <button
            onClick={() => navigate("/checkin" + window.location.search)}
            className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold text-sm hover:bg-gray-400 transition-colors"
          >
            BACK TO HOME
          </button>
        </div>
      </div>
    </div>
  );
}